import React from 'react'
import styled from 'styled-components';

const Card = styled.div`
  width: 330px;
  height: 490px;
  background: ${({ theme }) => theme.glass};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.glass_border};
  overflow: hidden;
  transition: all 0.4s ease-in-out;
  padding: 26px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 20px;
    padding: 2px;
    background: ${({ theme }) => theme.gradient_primary};
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${({ theme }) => theme.gradient_primary};
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover {
    transform: translateY(-12px);
    box-shadow: 0 30px 60px ${({ theme }) => theme.shadow_primary},
                0 0 80px ${({ theme }) => theme.shadow_accent};
    border-color: transparent;
  }

  &:hover::before {
    opacity: 1;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 180px;
  background: ${({ theme }) => theme.card};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
  object-fit: cover;
  transition: all 0.4s ease;
  border: 1px solid ${({ theme }) => theme.glass_border};

  ${Card}:hover & {
    transform: scale(1.03);
    box-shadow: 0 12px 30px ${({ theme }) => theme.shadow_primary};
  }
`;

const Tags = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
  align-items: center;
`;

const Tag = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.primary};
  background: ${({ theme }) => theme.primary + '15'};
  padding: 5px 12px;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 1px solid transparent;

  &:hover {
    background: ${({ theme }) => theme.primary + '30'};
    border-color: ${({ theme }) => theme.primary + '50'};
    transform: translateY(-2px);
  }
`;

const Details = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0px;
  padding: 0px 2px;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin-top: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  transition: all 0.3s ease;
  
  ${Card}:hover & {
    color: ${({ theme }) => theme.primary};
  }
`;

const Date = styled.p`
  font-size: 12px;
  font-weight: 500;
  margin-left: 2px;
  margin-top: 4px;
  color: ${({ theme }) => theme.text_secondary};
  font-family: 'JetBrains Mono', monospace;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const Description = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.text_secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  margin-top: 8px;
  max-width: 100%;
  line-height: 1.5;
`;

const Members = styled.div`
  display: flex;
  align-items: center;
  padding-left: 10px;
`;

const Avatar = styled.img`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  margin-left: -10px;
  border: 2px solid ${({ theme }) => theme.card};
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1) translateY(-4px);
    z-index: 10;
    box-shadow: 0 8px 20px ${({ theme }) => theme.shadow_primary};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
  margin-bottom: 8px;
  padding: 0 4px;
`;

const Button = styled.a`
  flex: 1;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
  padding: 10px 16px;
  border-radius: 10px;
  background: transparent;
  border: 2px solid ${({ theme }) => theme.primary};
  cursor: pointer;
  text-decoration: none;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.gradient_primary};
    transition: left 0.4s ease;
    z-index: -1;
  }

  &:hover {
    color: ${({ theme }) => theme.bg};
    border-color: transparent;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px ${({ theme }) => theme.shadow_primary};
  }
  
  &:hover::before {
    left: 0;
  }
`;

const Projectcard = ({ project }) => {
  return (
    <Card>
      <Image src={project.image} alt={project.title} />
      <Tags>
        {project.tags.map((tag, index) => (
          <Tag key={index}>{tag}</Tag>
        ))}
      </Tags>
      <Details>
        <Title>{project.title}</Title>
        <Date>{project.date}</Date>
        <Description>{project.description}</Description>
      </Details>
      <Members>
        {project.member?.map((member, index) => (
          <Avatar key={index} src={member.img} alt="member" />
        ))}
      </Members>
      <ButtonContainer>
        <Button href={project.github} target="_blank">Github</Button>
        <Button href={project.webapp} target="_blank">Live Demo</Button>
      </ButtonContainer>
    </Card>
  )
}

export default Projectcard;